import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import applicationService from '../services/applicationService';
import StatusBadge from '../components/StatusBadge';
import { tokenStorage } from '../utils/tokenStorage';
import ApplicationDetailsModal from '../components/ApplicationDetailsModal';

const ApplicationsListPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterOptions, setFilterOptions] = useState({
    stages: [],
    countries: [],
    jobPositions: [],
    locations: [],
    genders: [],
    paymentStatuses: [],
    durations: [],
    workExperienceRanges: []
  });
  const [filters, setFilters] = useState({
    currentStage: '',
    destinationCountry: '',
    jobPosting: '',
    paymentStatus: '',
    gender: '',
    currentLocation: '',
    workExperience: '',
    duration: '',
    page: 1,
    limit: 20
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  useEffect(() => {
    // Check authentication status
    if (!tokenStorage.hasToken()) {
      console.warn('No authentication token found');
      navigate('/login');
      return;
    }
    
    // Load filter options and applications
    loadFilterOptions();
    fetchApplications();
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [filters]);

  const loadFilterOptions = async () => {
    try {
      console.log('Loading filter options...');
      const response = await applicationService.getFilterOptions();
      console.log('Filter options response:', response);
      
      if (response.success) {
        console.log('Filter options loaded:', {
          stages: response.data.stages?.length || 0,
          countries: response.data.countries?.length || 0,
          jobPositions: response.data.jobPositions?.length || 0,
          locations: response.data.locations?.length || 0,
          genders: response.data.genders?.length || 0,
          paymentStatuses: response.data.paymentStatuses?.length || 0
        });
        console.log('Location options:', response.data.locations);
        console.log('Gender options:', response.data.genders);
        setFilterOptions(response.data);
      } else {
        console.error('Failed to load filter options:', response.error);
        // Set default filter options if API fails
        setFilterOptions({
          stages: [
            { value: 'APPLIED', label: 'Applied' },
            { value: 'SHORTLISTED', label: 'Shortlisted' },
            { value: 'AGREEMENT', label: 'Agreement' },
            { value: 'INTERVIEW', label: 'Interview' },
            { value: 'OFFER_LETTER', label: 'Offer Letter' },
            { value: 'PAYMENT', label: 'Payment' },
            { value: 'DOCUMENT_VERIFICATION', label: 'Document Verification' },
            { value: 'EXAM', label: 'Exam' },
            { value: 'MEDICAL', label: 'Medical' },
            { value: 'TPC_PRACTISING', label: 'TPC Practising' },
            { value: 'ESD_APPROVAL', label: 'ESD Approval' },
            { value: 'VISA_PROCESS', label: 'Visa Process' },
            { value: 'TICKET', label: 'Ticket' },
            { value: 'DEPARTURE', label: 'Departure' },
            { value: 'COMPLETED', label: 'Completed' },
            { value: 'REJECTED', label: 'Rejected' }
          ],
          countries: [],
          jobPositions: [],
          locations: [],
          genders: [
            { value: 'Male', label: 'Male' },
            { value: 'Female', label: 'Female' }
          ],
          paymentStatuses: [
            { value: 'Payment Not Done', label: 'Payment Not Done' },
            { value: 'Partial Payment', label: 'Partial Payment' },
            { value: 'Successful', label: 'Successful' }
          ],
          durations: [
            { value: 'last_2_weeks', label: 'Last 2 weeks' },
            { value: 'last_1_month', label: 'Last 1 month' },
            { value: 'last_3_months', label: 'Last 3 months' },
            { value: 'last_6_months', label: 'Last 6 months' },
            { value: 'last_1_year', label: 'Last 1 year' },
            { value: 'before_1_year', label: 'Before 1 year' }
          ],
          workExperienceRanges: [
            { value: 'less_than_1', label: 'Less than 1 year' },
            { value: '1_to_4', label: '1 to 4 years' },
            { value: '5_to_10', label: '5 to 10 years' },
            { value: '10_to_15', label: '10 to 15 years' },
            { value: '15_to_20', label: '15 to 20 years' },
            { value: '20_to_25', label: '20 to 25 years' },
            { value: '25_plus', label: '25+ years' }
          ]
        });
      }
    } catch (err) {
      console.error('Failed to load filter options:', err);
      
      // Check if it's an authentication/authorization error
      if (err.response?.status === 401) {
        console.error('Authentication failed for filter options');
      } else if (err.response?.status === 403) {
        console.error('Not authorized to access filter options');
      }
      
      // Set default filter options as fallback
      setFilterOptions({
        stages: [
          { value: 'APPLIED', label: 'Applied' },
          { value: 'SHORTLISTED', label: 'Shortlisted' },
          { value: 'AGREEMENT', label: 'Agreement' },
          { value: 'INTERVIEW', label: 'Interview' },
          { value: 'OFFER_LETTER', label: 'Offer Letter' },
          { value: 'PAYMENT', label: 'Payment' },
          { value: 'DOCUMENT_VERIFICATION', label: 'Document Verification' },
          { value: 'EXAM', label: 'Exam' },
          { value: 'MEDICAL', label: 'Medical' },
          { value: 'TPC_PRACTISING', label: 'TPC Practising' },
          { value: 'ESD_APPROVAL', label: 'ESD Approval' },
          { value: 'VISA_PROCESS', label: 'Visa Process' },
          { value: 'TICKET', label: 'Ticket' },
          { value: 'DEPARTURE', label: 'Departure' },
          { value: 'COMPLETED', label: 'Completed' },
          { value: 'REJECTED', label: 'Rejected' }
        ],
        countries: [],
        jobPositions: [],
        locations: [],
        genders: [
          { value: 'Male', label: 'Male' },
          { value: 'Female', label: 'Female' }
        ],
        paymentStatuses: [
          { value: 'Payment Not Done', label: 'Payment Not Done' },
          { value: 'Partial Payment', label: 'Partial Payment' },
          { value: 'Successful', label: 'Successful' }
        ],
        durations: [
          { value: 'last_2_weeks', label: 'Last 2 weeks' },
          { value: 'last_1_month', label: 'Last 1 month' },
          { value: 'last_3_months', label: 'Last 3 months' },
          { value: 'last_6_months', label: 'Last 6 months' },
          { value: 'last_1_year', label: 'Last 1 year' },
          { value: 'before_1_year', label: 'Before 1 year' }
        ],
        workExperienceRanges: [
          { value: 'less_than_1', label: 'Less than 1 year' },
          { value: '1_to_4', label: '1 to 4 years' },
          { value: '5_to_10', label: '5 to 10 years' },
          { value: '10_to_15', label: '10 to 15 years' },
          { value: '15_to_20', label: '15 to 20 years' },
          { value: '20_to_25', label: '20 to 25 years' },
          { value: '25_plus', label: '25+ years' }
        ]
      });
    }
  };

  const parseWorkExperienceRange = (range) => {
    switch (range) {
      case 'less_than_1':
        return [0, 0];
      case '1_to_4':
        return [1, 4];
      case '5_to_10':
        return [5, 10];
      case '10_to_15':
        return [10, 15];
      case '15_to_20':
        return [15, 20];
      case '20_to_25':
        return [20, 25];
      case '25_plus':
        return [25, 999];
      default:
        return [null, null];
    }
  };

  const formatApplicationDisplayId = (application) => {
    if (application?.applicationId) return application.applicationId;

    const rawId = application?._id ? application._id.toString() : '';
    if (!rawId) return 'N/A';

    const country = (application?.destinationCountry || '').trim();
    const countryPrefix = country
      ? country.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase().padEnd(2, 'X')
      : 'XX';

    const stableSuffix = rawId.slice(-6).toUpperCase();
    return `${countryPrefix}${stableSuffix}`;
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      
      // Parse work experience range if selected
      const filterParams = { ...filters };
      
      if (filters.workExperience) {
        const [minExp, maxExp] = parseWorkExperienceRange(filters.workExperience);
        filterParams.minWorkExperience = minExp;
        filterParams.maxWorkExperience = maxExp;
        delete filterParams.workExperience;
      }

      // Ensure page and limit are integers
      filterParams.page = parseInt(filterParams.page) || 1;
      filterParams.limit = parseInt(filterParams.limit) || 20;

      console.log('Fetching applications with params:', filterParams);

      // Use only legacy applications
      const response = await applicationService.getApplications(filterParams);
      
      if (response.success) {
        setApplications(response.applications || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 1);
        setError('');
        console.log(`✅ Loaded ${response.applications?.length || 0} applications`);
      } else {
        setError(response.error || 'Failed to fetch applications');
        setApplications([]);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      
      // Handle different types of errors
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view applications.');
      } else if (err.response?.status === 400) {
        const errorMessage = err.response?.data?.message || 'Invalid request parameters';
        const validationErrors = err.response?.data?.errors;
        if (validationErrors) {
          console.error('Validation errors:', validationErrors);
          setError(`Validation error: ${validationErrors.map(e => e.message).join(', ')}`);
        } else {
          setError(errorMessage);
        }
      } else {
        setError('Failed to fetch applications. Please try again.');
      }
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
      page: 1 // Reset to first page when filters change
    });
  };

  const clearAllFilters = () => {
    setFilters({
      currentStage: '',
      destinationCountry: '',
      jobPosting: '',
      paymentStatus: '',
      gender: '',
      currentLocation: '',
      workExperience: '',
      duration: '',
      page: 1,
      limit: 20
    });
  };

  const hasActiveFilters = () => {
    return Object.keys(filters).some(key => 
      key !== 'page' && key !== 'limit' && filters[key] !== ''
    );
  };

  const handlePageChange = (newPage) => {
    setFilters({
      ...filters,
      page: newPage
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewApplication = (applicationId) => {
    setSelectedApplicationId(applicationId);
    setShowApplicationModal(true);
  };

  const handleCloseModal = () => {
    setShowApplicationModal(false);
    setSelectedApplicationId(null);
  };

  const handleNextApplication = () => {
    const currentIndex = applications.findIndex(app => app._id === selectedApplicationId);
    if (currentIndex < applications.length - 1) {
      setSelectedApplicationId(applications[currentIndex + 1]._id);
    }
  };

  const handlePreviousApplication = () => {
    const currentIndex = applications.findIndex(app => app._id === selectedApplicationId);
    if (currentIndex > 0) {
      setSelectedApplicationId(applications[currentIndex - 1]._id);
    }
  };

  const getCurrentApplicationIndex = () => {
    return applications.findIndex(app => app._id === selectedApplicationId);
  };

  const hasNextApplication = () => {
    const currentIndex = getCurrentApplicationIndex();
    return currentIndex >= 0 && currentIndex < applications.length - 1;
  };

  const hasPreviousApplication = () => {
    const currentIndex = getCurrentApplicationIndex();
    return currentIndex > 0;
  };

  if (loading && filters.page === 1) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background py-10 px-5 max-w-[1400px] mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
            <div className="w-12 h-12 border-4 border-gray-300 border-t-accent-green rounded-full animate-spin"></div>
            <p className="text-text-secondary text-[15px]">Loading applications...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-background py-10 px-5 max-w-[1400px] mx-auto md:py-5">
        <div className="mb-8">
          <div>
            <h1 className="m-0 mb-1 text-[32px] font-bold text-primary md:text-2xl">All Applications</h1>
            <p className="m-0 text-text-secondary text-[15px]">Manage and review candidate applications</p>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          {/* Header Row */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-primary">Filter Applications</h2>
            <div className="flex items-center gap-3">
              {hasActiveFilters() && (
                <button
                  onClick={clearAllFilters}
                  className="h-10 px-4 bg-red-100 text-red-700 border border-red-300 rounded-md text-sm font-semibold cursor-pointer transition-all duration-200 hover:bg-red-200"
                >
                  Clear All Filters
                </button>
              )}
              <div className="text-text-secondary text-sm whitespace-nowrap">
                <span>Showing {applications.length} of {total} applications</span>
              </div>
            </div>
          </div>

          {/* All Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Stage Filter */}
            <div>
              <label htmlFor="currentStage" className="block font-semibold text-primary text-sm mb-2">Stage:</label>
              <select
                id="currentStage"
                name="currentStage"
                value={filters.currentStage}
                onChange={handleFilterChange}
                className="h-10 px-4 w-full border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:border-accent-green focus:ring-1 focus:ring-accent-green"
              >
                <option value="">All Stages</option>
                {filterOptions.stages.map(stage => (
                  <option key={stage.value} value={stage.value}>
                    {stage.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Applied Date Filter */}
            <div>
              <label htmlFor="duration" className="block font-semibold text-primary text-sm mb-2">Applied Date:</label>
              <select
                id="duration"
                name="duration"
                value={filters.duration}
                onChange={handleFilterChange}
                className="h-10 px-4 w-full border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:border-accent-green focus:ring-1 focus:ring-accent-green"
              >
                <option value="">All Time</option>
                {filterOptions.durations.map(duration => (
                  <option key={duration.value} value={duration.value}>
                    {duration.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Destination Country Filter */}
            <div>
              <label htmlFor="destinationCountry" className="block font-semibold text-primary text-sm mb-2">Destination Country:</label>
              <select
                id="destinationCountry"
                name="destinationCountry"
                value={filters.destinationCountry}
                onChange={handleFilterChange}
                className="h-10 px-4 w-full border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:border-accent-green focus:ring-1 focus:ring-accent-green"
              >
                <option value="">All Countries</option>
                {filterOptions.countries.map(country => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Job Position Filter */}
            <div>
              <label htmlFor="jobPosting" className="block font-semibold text-primary text-sm mb-2">Job Position:</label>
              <select
                id="jobPosting"
                name="jobPosting"
                value={filters.jobPosting}
                onChange={handleFilterChange}
                className="h-10 px-4 w-full border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:border-accent-green focus:ring-1 focus:ring-accent-green"
              >
                <option value="">All Positions</option>
                {filterOptions.jobPositions.map(job => (
                  <option key={job.value} value={job.value}>
                    {job.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Status Filter */}
            <div>
              <label htmlFor="paymentStatus" className="block font-semibold text-primary text-sm mb-2">Payment Status:</label>
              <select
                id="paymentStatus"
                name="paymentStatus"
                value={filters.paymentStatus}
                onChange={handleFilterChange}
                className="h-10 px-4 w-full border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:border-accent-green focus:ring-1 focus:ring-accent-green"
              >
                <option value="">All Payment Status</option>
                {filterOptions.paymentStatuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Gender Filter */}
            <div>
              <label htmlFor="gender" className="block font-semibold text-primary text-sm mb-2">Gender:</label>
              <select
                id="gender"
                name="gender"
                value={filters.gender}
                onChange={handleFilterChange}
                className="h-10 px-4 w-full border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:border-accent-green focus:ring-1 focus:ring-accent-green"
              >
                <option value="">All Genders</option>
                {filterOptions.genders.map(gender => (
                  <option key={gender.value} value={gender.value}>
                    {gender.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Current Location Filter */}
            <div>
              <label htmlFor="currentLocation" className="block font-semibold text-primary text-sm mb-2">Current Location:</label>
              <select
                id="currentLocation"
                name="currentLocation"
                value={filters.currentLocation}
                onChange={handleFilterChange}
                className="h-10 px-4 w-full border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:border-accent-green focus:ring-1 focus:ring-accent-green"
              >
                <option value="">All Locations</option>
                {filterOptions.locations.map(location => (
                  <option key={location.value} value={location.value}>
                    {location.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Work Experience Filter */}
            <div>
              <label htmlFor="workExperience" className="block font-semibold text-primary text-sm mb-2">Work Experience:</label>
              <select
                id="workExperience"
                name="workExperience"
                value={filters.workExperience}
                onChange={handleFilterChange}
                className="h-10 px-4 w-full border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:border-accent-green focus:ring-1 focus:ring-accent-green"
              >
                <option value="">All Experience</option>
                {filterOptions.workExperienceRanges.map(range => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="py-3 px-4 rounded-lg text-sm mb-5 bg-[#FFEBEE] text-[#C62828] border border-[#EF9A9A]">
            {error}
          </div>
        )}

        {applications.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center py-10 px-5">
            <div className="text-6xl mb-4 opacity-50">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1"/>
              </svg>
            </div>
            <h2 className="m-0 mb-2 text-2xl font-semibold text-primary">No Applications Found</h2>
            <p className="m-0 mb-6 text-text-secondary text-[15px]">
              {hasActiveFilters() 
                ? 'No applications match the selected filters' 
                : 'No applications have been submitted yet'}
            </p>
            {hasActiveFilters() && (
              <button
                className="py-3 px-6 bg-accent-green text-white border-none rounded-lg font-semibold text-[15px] cursor-pointer transition-all duration-200 hover:bg-[#5a6b4d] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(107,127,92,0.3)]"
                onClick={clearAllFilters}
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.1)] overflow-hidden mb-8">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-background">
                    <tr>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Applied Date</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Application ID</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Candidate Name</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Location</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Destination</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Job Position</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Status</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Gender</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Experience</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((application) => {
                      const formatDate = (dateString) => {
                        const date = new Date(dateString);
                        return date.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });
                      };

                      // Helper function to get candidate field with fallback
                      const getCandidateField = (primaryPath, fallbackPath) => {
                        if (!application.candidateId) return 'N/A';
                        
                        // Try primary path first
                        const primaryValue = primaryPath.split('.').reduce((obj, key) => obj?.[key], application.candidateId);
                        if (primaryValue) return primaryValue;
                        
                        // Try fallback path
                        if (fallbackPath) {
                          const fallbackValue = fallbackPath.split('.').reduce((obj, key) => obj?.[key], application.candidateId);
                          if (fallbackValue) return fallbackValue;
                        }
                        
                        return 'N/A';
                      };

                      // Get candidate name
                      const getCandidateName = () => {
                        if (!application.candidateId) return 'N/A';
                        
                        // Try new structure first
                        const newName = getCandidateField('personalInformation.name');
                        if (newName && newName !== 'N/A') return newName;
                        
                        // Try legacy structure
                        const firstName = getCandidateField('firstName');
                        const lastName = getCandidateField('lastName');
                        if (firstName !== 'N/A' || lastName !== 'N/A') {
                          return `${firstName !== 'N/A' ? firstName : ''} ${lastName !== 'N/A' ? lastName : ''}`.trim();
                        }
                        
                        return 'N/A';
                      };

                      // Get candidate location
                      const getCandidateLocation = () => {
                        // Try to get state from personalInformation.location.state first
                        const state = getCandidateField('personalInformation.location.state');
                        if (state && state !== 'N/A') return state;
                        
                        // Fallback to legacy state field
                        const legacyState = getCandidateField('state');
                        if (legacyState && legacyState !== 'N/A') return legacyState;
                        
                        // If no state, show nationality with a prefix to distinguish
                        const nationality = getCandidateField('nationality');
                        if (nationality && nationality !== 'N/A') return `${nationality} (Country)`;
                        
                        return 'Not Specified';
                      };

                      return (
                        <tr key={application._id} className="border-b border-gray-100 transition-colors duration-200 hover:bg-gray-50">
                          <td className="p-4 text-primary">
                            <div className="text-sm">
                              {formatDate(application.appliedAt)}
                            </div>
                          </td>
                          <td className="p-4 text-primary">
                            <div className="text-sm font-mono">
                              {formatApplicationDisplayId(application)}
                            </div>
                          </td>
                          <td className="p-4 text-primary">
                            <div className="text-sm font-medium">
                              {getCandidateName()}
                            </div>
                          </td>
                          <td className="p-4 text-primary">
                            <div className="text-sm">
                              {getCandidateLocation()}
                            </div>
                          </td>
                          <td className="p-4 text-primary">
                            <div className="text-sm font-medium">
                              {application.destinationCountry || 'Not Specified'}
                            </div>
                          </td>
                          <td className="p-4 text-primary">
                            <div className="font-medium text-sm">
                              {application.jobPosting || 'N/A'}
                            </div>
                          </td>
                          <td className="p-4 text-primary">
                            <StatusBadge stage={getStageDisplayName(application.currentStage)} />
                          </td>
                          <td className="p-4 text-primary">
                            <div className="text-sm">
                              {getCandidateField('personalInformation.gender', 'gender')}
                            </div>
                          </td>
                          <td className="p-4 text-primary">
                            <div className="text-sm">
                              {getCandidateField('professionalDetails.workExperience', 'experience')}
                            </div>
                          </td>
                          <td className="p-4 text-primary">
                            <div className="flex gap-2">
                              <button
                                className="bg-accent-green text-white border-none py-2 px-4 rounded-md cursor-pointer text-[0.9rem] transition-all duration-300 hover:bg-[#5a6e4d] hover:-translate-y-0.5"
                                onClick={() => handleViewApplication(application._id)}
                              >
                                View Details
                              </button>
                              <button
                                className="bg-blue-600 text-white border-none py-2 px-4 rounded-md cursor-pointer text-[0.9rem] transition-all duration-300 hover:bg-blue-700 hover:-translate-y-0.5"
                                onClick={() => navigate(`/applications/${application._id}`)}
                              >
                                Full Details
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 p-5 md:flex-col">
                <button
                  className="py-2.5 px-5 bg-white border border-gray-300 rounded-lg font-semibold text-sm cursor-pointer transition-all duration-200 text-primary hover:bg-accent-green hover:text-white hover:border-accent-green disabled:opacity-40 disabled:cursor-not-allowed md:w-full"
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 1 || loading}
                >
                  ← Previous
                </button>
                
                <div className="font-semibold text-primary text-sm">
                  Page {filters.page} of {totalPages}
                </div>

                <button
                  className="py-2.5 px-5 bg-white border border-gray-300 rounded-lg font-semibold text-sm cursor-pointer transition-all duration-200 text-primary hover:bg-accent-green hover:text-white hover:border-accent-green disabled:opacity-40 disabled:cursor-not-allowed md:w-full"
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page === totalPages || loading}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}

        {/* Application Details Modal */}
        {showApplicationModal && selectedApplicationId && (
          <ApplicationDetailsModal
            applicationId={selectedApplicationId}
            onClose={handleCloseModal}
            onNext={handleNextApplication}
            onPrevious={handlePreviousApplication}
            hasNext={hasNextApplication()}
            hasPrevious={hasPreviousApplication()}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default ApplicationsListPage;

// Map backend stage to display name for badge
const getStageDisplayName = (stage) => {
  if (!stage) return '';
  const map = {
    REGISTRATION: 'APPLIED',
    HR_SHORTLISTING: 'SHORTLISTED',
    AGREEMENT: 'AGREEMENT',
    APPLICATION: 'APPLICATION',
    INTERVIEW: 'INTERVIEW',
    OFFER_LETTER: 'OFFER LETTER',
    PAYMENT: 'PAYMENT',
    DOCUMENT_VERIFICATION: 'DOCUMENT VERIFICATION',
    EXAM: 'EXAM',
    MEDICAL: 'MEDICAL',
    TPC_PRACTISING: 'TPC PRACTISING',
    ESD_APPROVAL: 'ESD APPROVAL',
    VISA_PROCESS: 'VISA PROCESS',
    TICKET: 'TICKET',
    DEPARTURE: 'DEPARTURE',
    COMPLETED: 'COMPLETED',
    REJECTED: 'REJECTED',
  };
  return map[stage] || stage.replace(/_/g, ' ');
};